import { setUpReducer } from '../../helpers/useReducerUtil'
import {
  NoBrokerSet,
  UserEditorData,
  UserInfo
} from '../../store/userEditorData/types'

type UserInfoKey = KeysWithValsOfType<UserEditorData, UserInfo>

/*
Brokers, salespeople, account and classification are held by name because
that's what the dropdowns select on. Property names must not contain "_":
setUpReducer's update action type is split on it.
*/
export type WorkingUser = Omit<
  UserEditorData,
  | 'id'
  | 'possibleUserClassifications'
  | 'possibleCustomerRecords'
  | 'possibleBrokers'
  | 'cust'
  | 'userClassification'
  | 'subAccounts'
  | UserInfoKey
> &
  Record<UserInfoKey, string> & {
    account: string
    userClassification: string
    enabledSubAccountIds: number[]
    defaultSubAccountId: number | undefined
  }

export const { reducer, initState, updateActionCreator } =
  setUpReducer<WorkingUser>()

type BrokerPctKey = Extract<`${UserInfoKey}Pct`, keyof WorkingUser>
interface BrokerRow {
  label: string
  slots: ReadonlyArray<readonly [UserInfoKey, BrokerPctKey]>
}

export const brokerRows: readonly BrokerRow[] = [
  {
    label: '#1 Default Broker(s) who Cover:',
    slots: [
      ['broker1A', 'broker1APct'],
      ['broker1B', 'broker1BPct'],
      ['broker1C', 'broker1CPct']
    ]
  },
  {
    label: '#2 Default Broker(s) who Cover:',
    slots: [
      ['broker2A', 'broker2APct'],
      ['broker2B', 'broker2BPct'],
      ['broker2C', 'broker2CPct']
    ]
  },
  {
    label: '#3 Default Broker(s) who Cover:',
    slots: [
      ['broker3A', 'broker3APct'],
      ['broker3B', 'broker3BPct'],
      ['broker3C', 'broker3CPct']
    ]
  }
]

export const salesPersonKeys: readonly UserInfoKey[] = [
  'salesPersonWhoCovers1',
  'salesPersonWhoCovers2',
  'salesPersonWhoCovers3'
]

const userInfoKeys: UserInfoKey[] = [
  ...brokerRows.flatMap((row) => row.slots.map(([name]) => name)),
  ...salesPersonKeys
]

export const toWorkingUser = (user: UserEditorData): WorkingUser => {
  const {
    id: _id,
    possibleUserClassifications,
    possibleCustomerRecords,
    possibleBrokers: _possibleBrokers,
    cust,
    userClassification,
    subAccounts,
    ...rest
  } = user
  const names = Object.fromEntries(
    userInfoKeys.map((key) => [key, user[key]?.userName ?? ''])
  ) as Record<UserInfoKey, string>
  return {
    ...rest,
    ...names,
    loginPassword: '',
    account: (cust ?? possibleCustomerRecords[0])?.customerName ?? '',
    userClassification:
      (userClassification ?? possibleUserClassifications[0])?.userClassName ??
      '',
    enabledSubAccountIds: subAccounts
      .filter((sa) => sa.enabled)
      .map((sa) => sa.custId),
    defaultSubAccountId: subAccounts.find((sa) => sa.isDefault)?.custId
  }
}

export const toggleSubAccount = (ids: number[], custId: number) =>
  ids.includes(custId) ? ids.filter((id) => id !== custId) : [...ids, custId]

export const toSavePayload = (
  working: WorkingUser & { isChanged?: boolean },
  user: UserEditorData
): { payload: UserEditorData } | { error: string } => {
  const cust = user.possibleCustomerRecords.find(
    (c) => c.customerName === working.account
  )
  if (!cust) return { error: 'Invalid Account' }
  const userClassification = user.possibleUserClassifications.find(
    (uc) => uc.userClassName === working.userClassification
  )
  if (!userClassification) return { error: 'Invalid User classification' }
  if (
    working.defaultSubAccountId !== undefined &&
    !working.enabledSubAccountIds.includes(working.defaultSubAccountId)
  ) {
    return { error: 'Default sub-account must be enabled' }
  }

  const {
    account: _account,
    userClassification: _userClassification,
    enabledSubAccountIds,
    defaultSubAccountId,
    isChanged: _isChanged,
    ...rest
  } = working
  // possibleBrokers only lists brokers, so an unedited slot keeps the user it loaded with
  const userInfos = Object.fromEntries(
    userInfoKeys.map((key) => [
      key,
      working[key] === user[key]?.userName
        ? user[key]
        : user.possibleBrokers.find((b) => b.userName === working[key]) ??
          NoBrokerSet
    ])
  ) as Record<UserInfoKey, UserInfo>

  return {
    payload: {
      ...rest,
      ...userInfos,
      id: user.id,
      possibleUserClassifications: [],
      possibleCustomerRecords: [],
      possibleBrokers: [],
      cust,
      userClassification,
      subAccounts: user.subAccounts.map((sa) => ({
        ...sa,
        enabled: enabledSubAccountIds.includes(sa.custId),
        isDefault: sa.custId === defaultSubAccountId
      }))
    }
  }
}

export const currencySummary = (selected: string[], all: string[]) => {
  if (selected.length === 0) return undefined
  if (all.every((c) => selected.includes(c))) return 'All currencies'
  if (selected.length <= 3) return selected.join(', ')
  return `${selected.length} selected`
}
